/**
 * QnA Stream State Module
 * Manages SSE streaming for Admission QnA
 */

import { useCallback, useRef, useEffect } from 'react';
import { atom, useRecoilState } from 'recoil';
import { QnAStreamClient, type QnAStreamCallbacks } from '@/Data/Chat';
import { qnaState } from './index';
import type { QnaMessage, QnaAskParams } from 'ai-api/types/admission-qna';
import { randomString } from '@/Functions';

// ============ Types ============

interface QnAStreamState {
    isStreaming: boolean;
    streamingContent: string;
    statusContent: string;
    error: string | null;
}

// ============ Atom ============

export const qnaStreamState = atom<QnAStreamState>({
    key: 'qnaStreamState' + randomString(4),
    default: {
        isStreaming: false,
        streamingContent: '',
        statusContent: '',
        error: null,
    },
});

// ============ Hook ============

export function useQnAStream(sessionid: string | null) {
    const [streamState, setStreamState] = useRecoilState(qnaStreamState);
    const [, setState] = useRecoilState(qnaState);

    const clientRef = useRef<QnAStreamClient | null>(null);
    const contentRef = useRef<string>('');
    const sessionidRef = useRef<string>(sessionid ?? '');

    // Keep ref in sync
    useEffect(() => {
        sessionidRef.current = sessionid ?? '';
    }, [sessionid]);

    /**
     * Send message via streaming.
     * If sessionid is null, the stream will auto-create a session.
     * Returns the sessionid (useful when auto-created).
     */
    const sendMessage = useCallback(async (
        content: string,
        options?: { onSessionCreated?: (sessionid: string) => void }
    ): Promise<string | null> => {
        if (!content.trim()) return null;

        const currentSessionId = sessionidRef.current;

        // Add user message to state immediately
        const userMessage: QnaMessage = {
            messageid: `temp_${Date.now()}`,
            sessionid: currentSessionId || 'pending',
            role: 'user',
            content: content.trim(),
            category: null,
            is_genuine: null,
            quality: null,
            created_at: Math.floor(Date.now() / 1000),
        };

        if (currentSessionId) {
            setState(prev => ({
                ...prev,
                messages: {
                    ...prev.messages,
                    [currentSessionId]: [...(prev.messages[currentSessionId] ?? []), userMessage],
                },
            }));
        }

        // Initialize streaming
        if (!clientRef.current) {
            clientRef.current = new QnAStreamClient();
        }

        setStreamState({
            isStreaming: true,
            streamingContent: '',
            statusContent: '',
            error: null,
        });
        contentRef.current = '';

        let resolvedSessionId = currentSessionId;

        const callbacks: QnAStreamCallbacks = {
            onSession: (newSessionId) => {
                resolvedSessionId = newSessionId;
                sessionidRef.current = newSessionId;

                // If session was auto-created, move pending messages and notify
                if (!currentSessionId) {
                    setState(prev => ({
                        ...prev,
                        messages: {
                            ...prev.messages,
                            [newSessionId]: [{ ...userMessage, sessionid: newSessionId }],
                        },
                    }));
                }

                options?.onSessionCreated?.(newSessionId);
            },
            onText: (text) => {
                contentRef.current += text;
                setStreamState(prev => ({
                    ...prev,
                    streamingContent: contentRef.current,
                }));
            },
            onStatus: (content) => {
                setStreamState(prev => ({
                    ...prev,
                    statusContent: content,
                }));
            },
            onEnd: () => {
                // Add final assistant message
                const sid = resolvedSessionId || currentSessionId;
                if (contentRef.current && sid) {
                    const assistantMessage: QnaMessage = {
                        messageid: `assistant_${Date.now()}`,
                        sessionid: sid,
                        role: 'assistant',
                        content: contentRef.current,
                        category: null,
                        is_genuine: null,
                        quality: null,
                        created_at: Math.floor(Date.now() / 1000),
                    };

                    setState(prev => ({
                        ...prev,
                        messages: {
                            ...prev.messages,
                            [sid]: [...(prev.messages[sid] ?? []), assistantMessage],
                        },
                    }));
                }

                setStreamState({
                    isStreaming: false,
                    streamingContent: '',
                    statusContent: '',
                    error: null,
                });
            },
            onError: (error) => {
                console.error('QnA stream error:', error);
                setStreamState(prev => ({
                    ...prev,
                    isStreaming: false,
                    error: error.message,
                }));
            },
            onClose: () => {
                // Stream closed
            },
        };

        const askParams: QnaAskParams = {
            content: content.trim(),
            ...(currentSessionId ? { sessionid: currentSessionId } : {}),
        };

        await clientRef.current.ask(askParams, callbacks);

        return resolvedSessionId;
    }, [setState, setStreamState]);

    // Disconnect
    const disconnect = useCallback(() => {
        clientRef.current?.disconnect();
        setStreamState({
            isStreaming: false,
            streamingContent: '',
            statusContent: '',
            error: null,
        });
    }, [setStreamState]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clientRef.current?.disconnect();
        };
    }, []);

    return {
        isStreaming: streamState.isStreaming,
        streamingContent: streamState.streamingContent,
        statusContent: streamState.statusContent,
        error: streamState.error,
        sendMessage,
        disconnect,
    };
}
